import { Text } from '@wfp/react';
import sortBy from 'lodash/sortBy';
import React, { Fragment, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { scroller } from 'react-scroll';

import { CRF23 } from 'src/constants';
import { changeViewStateAction } from 'src/redux/view/actions';
import { SECTION_DETAIL_DATA_KEY } from 'src/redux/view/constants';
import CommentListItem from './CommentListItem';

export const CommentList = ({
  user,
  permissions,
  sectionAPIKwargs,
  commentsContainerID,
  comments,
  selectedFilter = 'all',
  expandAll = true,
}) => {
  const viewData = useSelector(state => state.viewData);
  const is2023CRF = viewData.ACR_REPORTS_DETAIL?.data?.crf === CRF23;

  const multiNarratives = viewData.ACR_SECTIONS_DETAIL?.data?.narratives;
  const narrativeContent = useSelector(
    state => state.viewData[SECTION_DETAIL_DATA_KEY]?.data?.content,
  );
  const highlightComment = useSelector(
    state => state.viewState?.highlightComment,
  );

  const scrollSharedOptions = {
    duration: 800,
    delay: 0,
    smooth: 'easeInOutQuart',
    ignoreCancelEvents: true,
  };

  const dispatch = useDispatch();

  function removeHighlight() {
    dispatch(
      changeViewStateAction({
        highlightComment: undefined,
      }),
    );
  }

  useEffect(() => {
    if (!highlightComment) return;

    let timer;

    const findCommentOrReply = (comments, targetId) => {
      for (const comment of comments) {
        if (comment.pk.toString() === targetId) {
          return {
            comment,
            isReply: false,
            scrollId: comment.anchor || `comment-${comment.pk}`,
          };
        }

        if (comment.comments && comment.comments.length > 0) {
          for (const reply of comment.comments) {
            if (reply.pk.toString() === targetId) {
              return {
                comment: reply,
                isReply: true,
                scrollId: `comment-${reply.pk}`,
              };
            }
          }
        }
      }
      return null;
    };

    const findByAnchor = (comments, targetAnchor) => {
      for (const comment of comments) {
        if (comment.anchor === targetAnchor) {
          return {
            comment,
            isReply: false,
            scrollId: targetAnchor,
          };
        }
      }
      return null;
    };

    let foundItem = findCommentOrReply(orderedComments, highlightComment);

    if (!foundItem) {
      foundItem = findByAnchor(orderedComments, highlightComment);
    }

    if (foundItem) {
      timer = setTimeout(() => {
        removeHighlight();
      }, 5000);

      const isReplyInClosedComment =
        foundItem.isReply &&
        orderedComments.find(
          comment =>
            comment.comments?.some(
              reply => reply.pk.toString() === highlightComment,
            ) && comment.closed,
        );
      const scrollDelay = isReplyInClosedComment ? 200 : 0;

      setTimeout(() => {
        if (document.documentElement.scrollTop === 0) {
          scroller.scrollTo('section-wrapper-main', scrollSharedOptions);
        }

        scroller.scrollTo(foundItem.scrollId, {
          ...scrollSharedOptions,
          containerId: 'acr-comments-wrapper',
          offset: -50,
        });
      }, scrollDelay);
    }

    return () => {
      clearTimeout(timer);
    };
  }, [highlightComment, orderedComments, comments]);

  function filterComments(data) {
    if (selectedFilter === 'all') {
      return data;
    }
    if (selectedFilter === 'opened') {
      return data.filter(comment => !comment.closed);
    }
    if (selectedFilter === 'important') {
      return data.filter(comment => comment.important);
    }
    if (selectedFilter === 'owner') {
      return data.filter(comment => comment.user.username === user);
    }
    return data;
  }

  function getAnchorsList(content) {
    if (!content) return [];

    let narrativeArray = content.split('id="');
    narrativeArray.shift();
    narrativeArray = narrativeArray.map(item => item.split('" name', 1)[0]);
    return narrativeArray;
  }

  function getSortedLinkedComments(linkedComments = [], anchorsList = []) {
    return linkedComments.sort(
      (a, b) => anchorsList.indexOf(a.anchor) - anchorsList.indexOf(b.anchor),
    );
  }

  const orderedComments = useMemo(() => {
    const filteredComments = filterComments(comments);

    let unlinkedComments = [];
    let linkedComments = [];

    if (!is2023CRF) {
      const narrativeArray = getAnchorsList(narrativeContent);
      filteredComments.forEach(comment => {
        if (
          comment.anchor !== null &&
          narrativeArray.includes(comment.anchor)
        ) {
          linkedComments.push(comment);
        } else {
          unlinkedComments.push(comment);
        }
      });

      linkedComments = getSortedLinkedComments(linkedComments, narrativeArray);

      return [...linkedComments, ...unlinkedComments];
    }

    const multiNarrativesOrdered = sortBy(multiNarratives, 'order');
    multiNarrativesOrdered.forEach(narrative => {
      let multiNarrativeLinkedComments = [];
      const narrativeArray = getAnchorsList(narrative.content);

      filteredComments.forEach(comment => {
        if (
          comment.anchor !== null &&
          narrativeArray.includes(comment.anchor)
        ) {
          multiNarrativeLinkedComments.push({
            ...comment,
            narrativeLinkedTitle: narrative.title,
          });
        }
      });

      multiNarrativeLinkedComments = getSortedLinkedComments(
        multiNarrativeLinkedComments,
        narrativeArray,
      );

      linkedComments.push(...multiNarrativeLinkedComments);
    });

    unlinkedComments = filteredComments
      .filter(
        item => linkedComments.findIndex(comm => comm.pk === item.pk) === -1,
      )
      .map(comment => ({
        ...comment,
        narrativeLinkedTitle: 'Untagged comments',
      }));

    return [...linkedComments, ...unlinkedComments];
  }, [narrativeContent, comments, selectedFilter]);

  const shouldHighlight = comment => {
    return (
      comment.anchor === highlightComment ||
      comment.pk.toString() === highlightComment
    );
  };

  return (
    <>
      {orderedComments?.map((comment, index) => (
        <Fragment key={comment.pk}>
          {comment.narrativeLinkedTitle &&
            (index === 0 ||
              orderedComments[index - 1].narrativeLinkedTitle !==
                comment.narrativeLinkedTitle) && (
              <div
                className={`comment-item-linked-narrative-title${
                  index === 0 ? ' first-narrative-title' : ''
                }`}
              >
                <Text kind="h6">{comment.narrativeLinkedTitle}</Text>
              </div>
            )}
          <CommentListItem
            activeUser={user}
            permissions={permissions}
            sectionAPIKwargs={sectionAPIKwargs}
            commentsContainerID={commentsContainerID}
            id={comment.pk}
            data={comment}
            highlightIsHovered={shouldHighlight(comment)}
            highlightComment={highlightComment}
            expandAll={expandAll}
          />
        </Fragment>
      ))}
    </>
  );
};

export default CommentList;
